import { isEqual, isNil } from 'lodash';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { JsonStorage } from '@shared/util/storage/JsonStorage';
import {
  type EditableViewTemplate,
  getEditableViewTemplate,
  getViewTemplateStorageKey,
} from './EditableViewTemplate';
import { useViewTemplateActions, useViewTemplateState } from './ViewTemplateContext';

const VIEW_TEMPLATE_TTL = 1000 * 60 * 60 * 24; // 1 day

interface PreserveViewTemplateProps {
  setIsModified: Dispatch<SetStateAction<boolean>>;
}

export function PreserveViewTemplate({ setIsModified }: PreserveViewTemplateProps) {
  const { viewTemplate, savedViewTemplate } = useViewTemplateState();
  const { setViewTemplate } = useViewTemplateActions();
  const storageKey = getViewTemplateStorageKey(viewTemplate.id);

  useEffect(() => {
    const edited = getEditableViewTemplate(viewTemplate);
    const saved = getEditableViewTemplate(savedViewTemplate);
    const isChanged = !isEqual(edited, saved);
    setIsModified(isChanged);
    if (isChanged) {
      // while changing views, can sometimes get a false positive, so delay saving
      const timer = setTimeout(() => {
        JsonStorage.set(storageKey, edited, VIEW_TEMPLATE_TTL);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      JsonStorage.remove(storageKey);
    }
  }, [viewTemplate, savedViewTemplate, storageKey, setIsModified]);

  useEffect(() => {
    // load viewTemplate from storage
    async function loadStoredViewTemplate() {
      const storedViewTemplate = await JsonStorage.get<EditableViewTemplate>(storageKey);
      if (!isNil(storedViewTemplate)) {
        setViewTemplate(prev => ({ ...prev, ...storedViewTemplate }));
      }
    }
    loadStoredViewTemplate();
  }, [storageKey, setViewTemplate]);

  return null;
}
